import { useState } from 'react';
import { Send, Loader2, CheckCircle2, AlertTriangle, Clock, RadioTower, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';
import { Input } from '../ui/input';

const TONE = {
  sent: 'text-emerald-300', partial: 'text-amber-300', pending: 'text-gray-400', failed: 'text-amber-300', skipped: 'text-amber-300',
};
const LABEL = { sent: 'Sent to LiveCosts', partial: 'Partly sent to LiveCosts', pending: 'Sending to LiveCosts…', failed: 'Not sent to LiveCosts', skipped: 'Not sent to LiveCosts' };

export const DELIVERY = {
  accepted: { label: 'awaiting delivery report', tone: 'text-gray-500' },
  queued: { label: 'queued', tone: 'text-gray-500' },
  delivered: { label: 'delivered', tone: 'text-emerald-300' },
  delayed: { label: 'delivery delayed', tone: 'text-amber-300' },
  bounced: { label: 'bounced', tone: 'text-red-300' },
  complained: { label: 'marked as spam', tone: 'text-red-300' },
  failed: { label: 'delivery failed', tone: 'text-red-300' },
};

export function DeliveryNote({ delivery, target = 'the mailbox', testId }) {
  if (!delivery?.status) return null;
  const d = DELIVERY[delivery.status] || { label: delivery.status, tone: 'text-gray-400' };
  const when = delivery.at ? new Date(delivery.at).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' }) : '';
  return (
    <span className={`${d.tone} ml-1`} title={delivery.reason || ''} data-testid={testId}>
      · {delivery.status === 'delivered' ? `delivered to ${target}` : d.label}{when ? ` ${when}` : ''}{delivery.reason ? ` — ${delivery.reason}` : ''}
    </span>
  );
}

/** LiveCosts delivery state of a posted invoice — compact (list row) or with detail (dialog). */
export function LiveCostsStatus({ item, compact = false }) {
  const lc = item?.livecosts;
  if (item?.status !== 'posted' || !lc) return null;
  const Icon = lc.status === 'sent' ? CheckCircle2 : lc.status === 'pending' ? Clock : AlertTriangle;
  const sentOnes = (lc.deliveries || []).filter(d => d.status === 'sent' && d.kind === 'livecosts');
  const to = sentOnes.map(d => `${d.company === 'CON' ? 'LC' : 'LD'} → ${d.destination_email}`).join(' · ');
  return (
    <span className={`inline-flex items-center gap-1 text-xs ${TONE[lc.status] || 'text-gray-400'}`} title={lc.failure_reason || to || ''} data-testid={`inbox-livecosts-status-${item.id}`}>
      <Icon className="w-3.5 h-3.5" /> {LABEL[lc.status] || lc.status}
      {!compact && lc.status === 'sent' && to && <span className="text-gray-500 ml-1">({to})</span>}
      {!compact && lc.status === 'sent' && sentOnes[0]?.delivery && <DeliveryNote delivery={sentOnes[0].delivery} target="LiveCosts" />}
      {!compact && lc.status !== 'sent' && lc.status !== 'pending' && lc.failure_reason && <span className="text-gray-500 ml-1 truncate max-w-[420px]">— {lc.failure_reason}</span>}
    </span>
  );
}

export function SendToLiveCostsButton({ api, item, onSent }) {
  const [busy, setBusy] = useState(false);
  if (item?.status !== 'posted') return null;
  const already = item.livecosts?.status === 'sent';
  const send = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/cost-inbox/${item.id}/send-livecosts`);
      const lc = r.data?.livecosts || {};
      if (lc.status === 'sent') toast.success('Invoice sent to LiveCosts');
      else toast.error(lc.failure_reason || 'Not sent to LiveCosts');
      onSent?.(lc);
    } catch (e) { toast.error(e.response?.data?.detail || 'Send failed'); }
    finally { setBusy(false); }
  };
  return (
    <Button variant="outline" onClick={send} disabled={busy} className="border-white/20 text-white bg-white/5 hover:bg-white/10" data-testid="inbox-send-livecosts-btn">
      {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />} {already ? 'Resend to LiveCosts' : 'Send to LiveCosts'}
    </Button>
  );
}

const XTONE = { sent: 'text-emerald-300', pending: 'text-gray-400', failed: 'text-amber-300', not_sent: 'text-gray-400' };
const XLABEL = { sent: 'Sent to Xero', pending: 'Sending to Xero…', failed: 'Not sent to Xero', not_sent: 'Not sent to Xero' };

/** Xero (email-to-bills) delivery state of a posted invoice — Not sent / Sent / Failed (+ mail-server delivery). */
export function XeroStatus({ item, compact = false }) {
  const xs = item?.xero_send;
  if (item?.status !== 'posted' || !xs) return null;
  const st = xs.status || 'not_sent';
  const Icon = st === 'sent' ? CheckCircle2 : st === 'pending' ? Clock : AlertTriangle;
  const when = xs.sent_at ? new Date(xs.sent_at).toLocaleString('en-GB') : '';
  const d = xs.delivery?.status ? DELIVERY[xs.delivery.status] : null;
  const title = st === 'sent'
    ? `Sent ${when}${xs.destination_email ? ` → ${xs.destination_email}` : ''}${xs.message_id ? ` · ref ${xs.message_id}` : ''}${d ? ` · ${d.label}` : ''}`
    : (xs.failure_reason || '');
  return (
    <span className={`inline-flex items-center gap-1 text-xs ${XTONE[st] || 'text-gray-400'}`} title={title} data-testid={`inbox-xero-status-${item.id}`}>
      <Icon className="w-3.5 h-3.5" /> {XLABEL[st] || st}
      {compact && st === 'sent' && xs.delivery?.status && xs.delivery.status !== 'accepted' && <span className={`${d?.tone || ''}`}>· {d?.label}</span>}
      {!compact && st === 'sent' && when && <span className="text-gray-500 ml-1">({when}{xs.compressed ? ' · PDF compressed' : ''})</span>}
      {!compact && st === 'sent' && <DeliveryNote delivery={xs.delivery} target="Xero's mail server" testId={`inbox-xero-delivery-${item.id}`} />}
      {!compact && st !== 'sent' && st !== 'pending' && xs.failure_reason && <span className="text-gray-500 ml-1 truncate max-w-[420px]">— {xs.failure_reason}</span>}
      {!compact && (xs.history?.length || 0) > 0 && <span className="text-gray-500 ml-1">· re-sent {xs.history.length}×</span>}
    </span>
  );
}

/** Send / retry a Xero send; once sent, offers an explicit confirmed re-send. */
export function SendToXeroButton({ api, item, onSent }) {
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [reason, setReason] = useState('');
  if (item?.status !== 'posted') return null;
  const st = (item.xero_send || {}).status;
  if (st === 'pending') return null;
  const sent = st === 'sent';
  const send = async (force) => {
    setBusy(true);
    try {
      const r = await api.post(`/cost-inbox/${item.id}/send-xero`, force ? { force_resend: true, reason } : {});
      if (r.data?.already_sent) toast.info('Already sent to Xero — not sent again');
      else if (r.data?.xero?.status === 'sent') toast.success(force ? 'Invoice re-sent to Xero' : 'Invoice sent to Xero');
      else toast.error(r.data?.xero?.failure_reason || 'Not sent to Xero');
      onSent?.(r.data?.xero);
    } catch (e) { toast.error(e.response?.data?.detail || 'Send failed'); }
    finally { setBusy(false); setConfirm(false); setReason(''); }
  };
  if (!sent) {
    return (
      <Button variant="outline" onClick={() => send(false)} disabled={busy} className="border-white/20 text-white bg-white/5 hover:bg-white/10" data-testid="inbox-send-xero-btn">
        {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />} Send to Xero
      </Button>
    );
  }
  return (
    <>
      <Button variant="ghost" onClick={() => setConfirm(true)} disabled={busy} className="text-gray-300 hover:text-white hover:bg-white/10" data-testid="inbox-resend-xero-btn">
        {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-2" />} Resend to Xero
      </Button>
      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent className="bg-[#2A3240] border-white/10 text-white" data-testid="inbox-resend-xero-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Send this invoice to Xero again?</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-300">
              Only do this if you've checked Xero → Bills to pay → <b>Drafts</b> and the bill isn't there. Sending twice creates a duplicate draft bill in Xero. The earlier send stays on record.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Input value={reason} onChange={e => setReason(e.target.value)} placeholder="Why? (optional — e.g. not in Xero drafts after 2 days)" className="bg-[#1E2530] border-white/15 text-white text-sm" data-testid="inbox-resend-xero-reason" />
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-white/20 text-gray-200 hover:bg-white/10 hover:text-white" data-testid="inbox-resend-xero-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => send(true)} className="bg-[#F5A623] text-[#1E2530] hover:bg-[#f7b84a]" data-testid="inbox-resend-xero-go">Yes, send again</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

/** Ask Resend for the latest delivery report of this invoice's emails. */
export function CheckDeliveryButton({ api, item, onChecked }) {
  const [busy, setBusy] = useState(false);
  if (item?.status !== 'posted') return null;
  const ids = [item.xero_send?.message_id, ...((item.xero_send?.history || []).map(h => h.message_id)), ...((item.livecosts?.deliveries || []).map(d => d.message_id))].filter(Boolean);
  if (!ids.length) return null;
  const check = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/cost-inbox/${item.id}/check-delivery`);
      const bad = (r.data?.results || []).find(x => ['bounced', 'failed', 'complained'].includes(x.status));
      if (bad) toast.error(`Delivery problem: ${DELIVERY[bad.status]?.label || bad.status}`);
      else toast.success(`Checked ${r.data?.checked} email${r.data?.checked === 1 ? '' : 's'} with Resend`);
      onChecked?.(r.data?.item);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not check delivery'); }
    finally { setBusy(false); }
  };
  return (
    <Button variant="ghost" onClick={check} disabled={busy} className="text-gray-300 hover:text-white hover:bg-white/10" title="Ask Resend whether the mail server accepted, delayed or bounced the email" data-testid="inbox-check-delivery-btn">
      {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RadioTower className="w-4 h-4 mr-2" />} Check delivery
    </Button>
  );
}
